import { Avatar, AvatarFallback, AvatarImage } from '@tempo-io/sui-avatar';
import { Badge } from '@tempo-io/sui-badge';
import { TooltipContent } from '@tempo-io/sui-tooltip';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemStatusBadge } from '@/components/ui/itemStatusBadge';
import { useGetItemStatusByIdQuery } from '@/lib/queries/common/useGetItemStatusesQuery';
import { useGetTeamQuery } from '@/lib/queries/teams/useGetTeamQuery';
import { useGetEstimationsByIds } from '@/lib/queries/work-integrations/useGetEstimations';
import { DateFormat, formatLocalDateWithFallback } from '@/lib/utils/common/dateFormats';
import { STALE_TIME_MS } from './TimelineItemConstants';
import type { TimelineItemType } from './useTimelineTableColumns';

export const TimelineItemTooltip = ({
  item,
  resizedWidth,
  isTooltipOpen,
  isPublishedMode,
  displayStartDate,
  displayEndDate,
  progress,
  hasProgress,
  mouseX,
}: {
  item: TimelineItemType;
  resizedWidth: number;
  isTooltipOpen: boolean;
  isPublishedMode: boolean;
  displayStartDate: string | null | undefined;
  displayEndDate: string | null | undefined;
  progress: number | null;
  hasProgress: boolean;
  mouseX: number;
}) => {
  const { t } = useTranslation();

  const tooltipAlign = useMemo(() => {
    const thirdWidth = resizedWidth / 3;
    if (mouseX < thirdWidth) return 'start';
    if (mouseX > resizedWidth - thirdWidth) return 'end';
    return 'center';
  }, [mouseX, resizedWidth]);

  const { data: teamData, isLoading: isTeamLoading } = useGetTeamQuery({
    teamId: item.teamId ?? undefined,
    options: {
      staleTime: STALE_TIME_MS,
      enabled: !!item.teamId && isTooltipOpen && !isPublishedMode,
    },
  });
  const { data: estimationsByIds, isLoading: isEstimationSchemeLoading } = useGetEstimationsByIds({
    estimationIds: new Set(item?.estimationId ? [item.estimationId] : []),
    enabled: !!item.estimationId && isTooltipOpen && !isPublishedMode,
    staleTime: STALE_TIME_MS,
  });
  const { data: itemStatuses, isLoading: isItemStatusesLoading } = useGetItemStatusByIdQuery({
    id: item.itemStatusId,
    options: {
      enabled: isTooltipOpen && !!item.itemStatusId && !isPublishedMode,
      staleTime: STALE_TIME_MS,
    },
  });

  return (
    <TooltipContent
      align={tooltipAlign}
      side="bottom"
      className="bg-bg-surface-base text-text-neutral-primary border-2 min-w-64 w-max max-w-100"
    >
      <div className="flex flex-col gap-2 p-2 w-full">
        <span className="font-bold text-base mb-2 w-full">{item.name}</span>

        <div className="border-b-2 text-text-neutral-secondary" />

        <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-2 items-center">
          {!isTeamLoading && teamData && (
            <>
              <strong>{t(($) => $.loopVisualization.layout.headers.fields.team)}:</strong>
              <Badge variant="secondary">
                <Avatar className="h-4 w-4">
                  <AvatarImage src={teamData.team.icon} />
                  <AvatarFallback className="bg-bg-neutral">
                    {teamData.team.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                {teamData.team.name}
              </Badge>
            </>
          )}
          {!isEstimationSchemeLoading && estimationsByIds && item.estimationId && (
            <>
              <strong>{t(($) => $.common.filters.labels.items.effortEstimate)}:</strong>
              <Badge variant="secondary">{estimationsByIds[item.estimationId]?.label}</Badge>
            </>
          )}
          {!isItemStatusesLoading && itemStatuses && (
            <>
              <strong>{t(($) => $.loopVisualization.layout.headers.fields.status)}:</strong>
              <ItemStatusBadge label={itemStatuses.name} color={itemStatuses.color} />
            </>
          )}
        </div>

        <div className="border-b-2 text-text-neutral-secondary" />

        <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-2 items-center">
          <strong>{t(($) => $.loopVisualization.layout.headers.fields.startDate)}:</strong>
          <span>
            {formatLocalDateWithFallback({
              dateString: displayStartDate,
              formatStr: DateFormat.MONTH_SHORT_DAY_SHORT_YEAR,
              fallback: t(($) => $.common.notAvailable),
            })}
          </span>

          <strong>{t(($) => $.loopVisualization.layout.headers.fields.endDate)}:</strong>
          <span>
            {formatLocalDateWithFallback({
              dateString: displayEndDate,
              formatStr: DateFormat.MONTH_SHORT_DAY_SHORT_YEAR,
              fallback: t(($) => $.common.notAvailable),
            })}
          </span>

          {hasProgress && (
            <>
              <strong>{t(($) => $.loopVisualization.layout.formatPanel.progressLabel)}:</strong>
              <span>{progress}%</span>
            </>
          )}
        </div>
      </div>
    </TooltipContent>
  );
};
